import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { PhotoProvider } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'
import { toast } from 'sonner'
import { AddPhotoIconButton, AddPhotoTile, PhotoThumbnail, PendingThumbnail } from './TaskPhotos'
import { useS3GetUploadPost, uploadThumbnailBestEffort } from '@/lib/hooks/s3'
import { isNative } from '@/lib/native-file-viewer'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const ACCEPT = 'image/*'

interface StagedPhotoGridProps {
  // S3 keys already persisted for this entity/category.
  s3Keys: string[]
  readOnly?: boolean
  onPendingChange?: (count: number) => void
  // Category (facility/preference key) is folded in by the wrapper component.
  generateS3Key: (entityId: string, fileName: string) => string
  addPhoto: (entityId: string, s3Key: string) => Promise<unknown>
  removePhoto: (entityId: string, s3Key: string) => Promise<unknown>
  /** How the add-photo trigger renders: 'tile' (default) is the square grid
      tile; 'icon' is a compact round camera button above the grid. */
  trigger?: 'tile' | 'icon'
}

export interface StagedPhotoGridHandle {
  // Upload pending additions and apply pending removals against the given
  // entity. The id is passed in (not closed over) so create mode can flush
  // against the id returned by the create mutation.
  flushPending: (entityId: string, onUploaded?: () => void) => Promise<void>
  pendingCount: () => number
}

interface PendingPhoto {
  id: string
  file: File
  previewUrl: string
}

/**
 * Generic staged multi-photo grid: stages additions/removals locally and
 * applies them on flushPending. Domain wrappers (FacilityPhotos,
 * PaxPreferencePhotos) bind the S3-key generator and add/remove mutations.
 */
export const StagedPhotoGrid = forwardRef<StagedPhotoGridHandle, StagedPhotoGridProps>(
  function StagedPhotoGrid(
    { s3Keys, readOnly = false, onPendingChange, generateS3Key, addPhoto, removePhoto, trigger = 'tile' },
    ref
  ) {
    const getUploadPost = useS3GetUploadPost()

    const fileInputRef = useRef<HTMLInputElement>(null)
    const [pending, setPending] = useState<PendingPhoto[]>([])
    const [pendingRemovals, setPendingRemovals] = useState<string[]>([])
    const [uploadingIds, setUploadingIds] = useState<Set<string>>(new Set())
    // s3Key → local object URL for photos uploaded this session, so thumbnails
    // render the in-memory blob instead of fetching a presigned URL.
    const [uploadedPreviews, setUploadedPreviews] = useState<Record<string, string>>({})

    const visibleS3Keys = s3Keys.filter((k) => !pendingRemovals.includes(k))

    useEffect(() => {
      onPendingChange?.(pending.length + pendingRemovals.length)
    }, [pending.length, pendingRemovals.length, onPendingChange])

    useEffect(() => {
      return () => {
        // Revoke any outstanding object URLs on unmount
        setPending((cur) => {
          cur.forEach((p) => URL.revokeObjectURL(p.previewUrl))
          return cur
        })
        setUploadedPreviews((cur) => {
          Object.values(cur).forEach((url) => URL.revokeObjectURL(url))
          return cur
        })
      }
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        pendingCount: () => pending.length + pendingRemovals.length,
        flushPending: async (entityId, onUploaded) => {
          if (pending.length === 0 && pendingRemovals.length === 0) return
          const uploadedIds = new Set<string>()
          const removedKeys = new Set<string>()
          let firstError: unknown = null

          for (const item of pending) {
            setUploadingIds((cur) => new Set(cur).add(item.id))
            try {
              const s3Key = generateS3Key(entityId, item.file.name)
              const { url, fields } = (await getUploadPost.mutateAsync({
                s3Key,
                contentType: item.file.type || 'application/octet-stream',
              } as never)) as { url: string; fields: Record<string, string> }

              const formData = new FormData()
              Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
              formData.append('file', item.file)

              const res = await fetch(url, { method: 'POST', body: formData })
              if (!res.ok) throw new Error(`Upload failed (${res.status})`)

              // Grid thumbnail next to the original, before the record exists
              // so the first render of the tile already finds it.
              await uploadThumbnailBestEffort(item.file, s3Key, getUploadPost.mutateAsync as never)

              await addPhoto(entityId, s3Key)
              uploadedIds.add(item.id)
              // Keep the blob URL keyed by its new s3Key so the server-backed
              // thumbnail can render it without a presigned-URL fetch.
              setUploadedPreviews((cur) => ({ ...cur, [s3Key]: item.previewUrl }))
              onUploaded?.()
            } catch (err) {
              if (!firstError) firstError = err
              const msg = err instanceof Error ? err.message : 'Upload failed'
              toast.error(`${item.file.name}: ${msg}`)
            } finally {
              setUploadingIds((cur) => {
                const next = new Set(cur)
                next.delete(item.id)
                return next
              })
            }
          }

          for (const s3Key of pendingRemovals) {
            try {
              await removePhoto(entityId, s3Key)
              removedKeys.add(s3Key)
            } catch (err) {
              if (!firstError) firstError = err
              const msg = err instanceof Error ? err.message : 'Failed to remove photo'
              toast.error(msg)
            }
          }

          setPending((cur) => cur.filter((p) => !uploadedIds.has(p.id)))
          setPendingRemovals((cur) => cur.filter((k) => !removedKeys.has(k)))
          if (firstError) throw firstError
        },
      }),
      [pending, pendingRemovals, generateS3Key, getUploadPost, addPhoto, removePhoto]
    )

    if (readOnly) {
      if (visibleS3Keys.length === 0) return null
      // Show every photo (no 4-item cap): a full 3-col grid of self-resolving
      // thumbnails. On web, PhotoProvider lets the lightbox swipe across all of
      // them; on native each tile opens the file viewer (PhotoProvider skipped,
      // matching TaskPhotos/ImageGrid).
      const grid = (
        <div className="grid grid-cols-3 gap-2">
          {visibleS3Keys.map((s3Key) => (
            <PhotoThumbnail key={s3Key} s3Key={s3Key} localUrl={uploadedPreviews[s3Key]} />
          ))}
        </div>
      )
      return isNative ? grid : <PhotoProvider>{grid}</PhotoProvider>
    }

    const handlePickFiles = () => {
      fileInputRef.current?.click()
    }

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      e.target.value = ''
      if (files.length === 0) return

      const accepted: PendingPhoto[] = []
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`)
          continue
        }
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`${file.name} exceeds the 20 MB limit`)
          continue
        }
        accepted.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          previewUrl: URL.createObjectURL(file),
        })
      }
      if (accepted.length > 0) {
        setPending((cur) => [...cur, ...accepted])
      }
    }

    const handleRemoveUploaded = (s3Key: string) => {
      setPendingRemovals((cur) => (cur.includes(s3Key) ? cur : [...cur, s3Key]))
    }

    const handleRemovePending = (id: string) => {
      setPending((cur) => {
        const next = cur.filter((p) => p.id !== id)
        const removed = cur.find((p) => p.id === id)
        // Don't revoke a URL that an uploaded thumbnail now renders from.
        if (removed && !Object.values(uploadedPreviews).includes(removed.previewUrl)) {
          URL.revokeObjectURL(removed.previewUrl)
        }
        return next
      })
    }

    // A pending tile is hidden once its uploaded s3Key shows up in s3Keys, so
    // during the flush there's never a duplicate or a gap.
    const uploadedPreviewUrls = new Set(
      visibleS3Keys.map((k) => uploadedPreviews[k]).filter(Boolean)
    )

    const visiblePending = pending.filter((p) => !uploadedPreviewUrls.has(p.previewUrl))

    const thumbnails = (
      <>
        {visibleS3Keys.map((s3Key) => (
          <PhotoThumbnail
            key={s3Key}
            s3Key={s3Key}
            localUrl={uploadedPreviews[s3Key]}
            onRemove={() => handleRemoveUploaded(s3Key)}
          />
        ))}
        {visiblePending.map((p) => (
          <PendingThumbnail
            key={p.id}
            previewUrl={p.previewUrl}
            uploading={uploadingIds.has(p.id)}
            onRemove={() => handleRemovePending(p.id)}
          />
        ))}
      </>
    )

    // 'icon' puts a compact trigger above the grid (and skips the empty grid
    // entirely); 'tile' keeps the classic trailing square inside the grid.
    const editableBody =
      trigger === 'icon' ? (
        <div className="flex flex-col gap-2">
          <AddPhotoIconButton onClick={handlePickFiles} />
          {(visibleS3Keys.length > 0 || visiblePending.length > 0) && (
            <div className="grid grid-cols-3 gap-2">{thumbnails}</div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {thumbnails}
          <AddPhotoTile onClick={handlePickFiles} />
        </div>
      )

    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={handleFilesSelected}
        />
        {isNative ? editableBody : <PhotoProvider>{editableBody}</PhotoProvider>}
      </>
    )
  }
)
